import { and, eq } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo } from 'react'
import { classifyUserActivity } from '@/hooks/monitor/classifyUserActivity'
import type { LocationCircle } from '@/hooks/useActiveLocations'
import { useStore } from '@/lib/pocketbase'
import { combineWithOr } from '@/lib/query-utils'
import { resolveWorkingHours } from '@/lib/working-hours'
import type { PathPoint } from '@/types/movement'
import type { Users } from '@/types/pbSchema'
import type { UserTrackingStatus } from '@/types/trackingStatus'

interface UseUsersTrackingStatusParams {
    userMap: Map<string, Users>
    orgId: string | undefined
    mostRecentLocationMap: Map<string, PathPoint | undefined>
    pathMap: Map<string, PathPoint[]>
    hasAnyMovementMap: Map<string, boolean>
    activeJobIdMap: Map<string, string | undefined>
    geofences: LocationCircle[]
    now: Date
}

export function useUsersTrackingStatus({
    userMap,
    orgId,
    mostRecentLocationMap,
    pathMap,
    hasAnyMovementMap,
    activeJobIdMap,
    geofences,
    now,
}: UseUsersTrackingStatusParams) {
    const [orgsCollection, userOrgCollection] = useStore('orgs', 'user_org')

    const normalizedUserIds = useMemo(() => {
        return Array.from(userMap.keys()).sort()
    }, [userMap])

    const { data: userOrgs = [], isLoading: isLoadingUserOrgs } = useLiveQuery(
        q =>
            normalizedUserIds.length > 0 && orgId
                ? q
                      .from({ userOrg: userOrgCollection })
                      .where(({ userOrg }) =>
                          combineWithOr(
                              normalizedUserIds.map(id =>
                                  and(eq(userOrg.user, id), eq(userOrg.org, orgId))
                              )
                          )
                      )
                : undefined,
        [normalizedUserIds, orgId]
    )

    const { data: orgRecords = [], isLoading: isLoadingOrg } = useLiveQuery(
        q =>
            orgId
                ? q.from({ org: orgsCollection }).where(({ org }) => eq(org.id, orgId))
                : undefined,
        [orgId]
    )

    const orgSettings = orgRecords[0] ?? null

    const userOrgMap = useMemo(() => {
        const map = new Map<string, (typeof userOrgs)[number]>()
        for (const uo of userOrgs) {
            map.set(uo.user, uo)
        }
        return map
    }, [userOrgs])

    const statusMap = useMemo(() => {
        const map = new Map<string, UserTrackingStatus>()
        for (const [userId, user] of userMap) {
            const userOrg = userOrgMap.get(userId)
            const workingHours = resolveWorkingHours(userOrg, orgSettings)
            const status = classifyUserActivity({
                userId,
                geoTrackingDisabled: Boolean(user.geo_tracking_disabled),
                mostRecentLocation: mostRecentLocationMap.get(userId),
                path: pathMap.get(userId) ?? [],
                hasAnyMovementToday: hasAnyMovementMap.get(userId) ?? false,
                activeJobId: activeJobIdMap.get(userId),
                workingHours,
                geofences,
                now,
            })
            map.set(userId, status)
        }
        return map
    }, [
        userMap,
        userOrgMap,
        orgSettings,
        mostRecentLocationMap,
        pathMap,
        hasAnyMovementMap,
        activeJobIdMap,
        geofences,
        now,
    ])

    return {
        statusMap,
        isPending: isLoadingOrg || isLoadingUserOrgs,
    }
}
